import { SelectionChangedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { AxiosError } from 'axios';
import { ChangeEvent, FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import isEqual from 'react-fast-compare';
import { useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import {
  DropdownWrapperMedium,
  InputWrapperLarge,
  InputWrapperMedium,
} from '../../../common/FormElementWrappers/FormElementWrappers.styles';
import Header from '../../../common/Header/Header';
import { TableRowWrapper, TableWrapper } from '../../../common/SideBySideTableLayout/SideBySideTableLayout.styles';
import UnsavedChangesModal from '../../../common/UnsavedChangesModal/UnsavedChangesModal';
import Dropdown from '../../../components/Dropdown/Dropdown';
import Input from '../../../components/Input/Input';
import { EditCellRenderer } from '../../../components/Table/EditCellRenderer/EditCellRenderer';
import Table from '../../../components/Table/Table';
import { showToast } from '../../../components/Toast/Toast';
import { STATUS, StatusKeys } from '../../../constants/ApiEnums';
import { useClassBases } from '../../../services/base/base.service';
import { ClassBase } from '../../../services/base/base.types';
import { useClasses } from '../../../services/class/class.service';
import { Class } from '../../../services/class/class.types';
import { useProducts } from '../../../services/product/product.service';
import { Product } from '../../../services/product/product.types';
import { useEditRecipe, useRecipe, useRecipeDetails } from '../../../services/recipe/recipe.service';
import { RawProdAssignedToRecipe } from '../../../services/recipe/recipe.types';
import { useClassSizes } from '../../../services/size/size.service';
import { ClassSize } from '../../../services/size/size.types';
import rem from '../../../utils/rem/rem';
import { useFetchErrorToast } from '../../../utils/useFetchErrorToasts/useFetchErrorToast';
import { genericErrorShowToastOptions } from '../../../utils/useGenericErrorMessage/useGenericErrorMessage';
import { H2WithMargin, HR, RecipeForm, RowWrapper, SaveButton } from './EditRecipe.styles';
import { RecipeFormValues } from './EditRecipe.types';
import RawProductSelectTable from "../../../components/RawProduct/RawProductSelectTable";

const EditRecipe: FC = () => {
  const queryClient = useQueryClient();
  const { specNo } = useParams();
  const _specNo = specNo as string;
  const availableGridRef = useRef<AgGridReact>(null);
  const selectedGridRef = useRef<AgGridReact>(null);

  const [formValues, setFormValues] = useState<RecipeFormValues>({
    description: '',
    longDescription: '',
    fmsProdNo: '',
    statusNo: '',
    moaClassNo: '',
    moaBaseNo: '',
    moaSizeNo: '',
    moaProductNo: '',
  });

  const [initialFormValues, setInitialFormValues] = useState<RecipeFormValues>({
    description: '',
    longDescription: '',
    fmsProdNo: '',
    statusNo: '',
    moaClassNo: '',
    moaBaseNo: '',
    moaSizeNo: '',
    moaProductNo: '',
  });

  const { data: recipe, isError: recipeError, refetch } = useRecipe(_specNo, true);
  const {
    data: recipeDetails,
    isError: recipeDetailsIsError,
    error: recipeDetailsError,
  } = useRecipeDetails(_specNo, true);

  const { data: classes, isError: classesError } = useClasses(true, 0);
  const { data: bases, isError: basesError } = useClassBases(
    { classNo: formValues.moaClassNo, status: 0 },
    formValues.moaClassNo !== ''
  );
  const { data: sizes, isError: sizesError } = useClassSizes(
    { classNo: formValues.moaClassNo, status: 0 },
    formValues.moaClassNo !== ''
  );
  const { data: products, isError: productsError } = useProducts(formValues.moaClassNo !== '', {
    classNo: +formValues.moaClassNo,
  });
  const { mutate, isLoading } = useEditRecipe(_specNo);

  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [selectedRawProds, setSelectedRawProds] = useState<RawProdAssignedToRecipe[]>(recipeDetails?.data || []);
  const [initialSelectedRawProds, setInitialSelectedRawProds] = useState<RawProdAssignedToRecipe[]>(
    recipeDetails?.data || []
  );

  const checkForUnsavedChanges = () => {
    if (
      formValues.description !== initialFormValues.description ||
      formValues.longDescription !== initialFormValues.longDescription ||
      formValues.fmsProdNo !== initialFormValues.fmsProdNo ||
      formValues.statusNo !== initialFormValues.statusNo ||
      formValues.moaClassNo !== initialFormValues.moaClassNo ||
      formValues.moaBaseNo !== initialFormValues.moaBaseNo ||
      formValues.moaSizeNo !== initialFormValues.moaSizeNo ||
      formValues.moaProductNo !== initialFormValues.moaProductNo
    ) {
      setUnsavedChanges(true);
      return;
    }

    if (initialSelectedRawProds && selectedRawProds) {
      if (initialSelectedRawProds.length === selectedRawProds.length) {
        const sortedInitialSelectedRps = [...initialSelectedRawProds]; // Deep copy initialSelectedRawProds
        sortedInitialSelectedRps.sort((a, b) => (a.moaRawProdNo > b.moaRawProdNo ? 1 : -1));
        const sortedSelectedRps = [...selectedRawProds]; // Deep copy selectedRawProds
        sortedSelectedRps.sort((a, b) => (a.moaRawProdNo > b.moaRawProdNo ? 1 : -1));
        if (!isEqual(sortedInitialSelectedRps, sortedSelectedRps)) {
          // Initial sRPs is sorted from DB
          setUnsavedChanges(true);
          return;
        } else {
          setUnsavedChanges(false);
          return;
        }
      } else {
        setUnsavedChanges(true);
        return;
      }
    }

    setUnsavedChanges(false);
  };

  useEffect(() => {
    checkForUnsavedChanges();
  }, [selectedRawProds, recipeDetails?.data, formValues]);

  const selectMatchingAvailableRawProds = (selectedRows: any[]) => {
    availableGridRef?.current?.api?.forEachNode((node) => {
      const matchingItem = selectedRows.find((rawProd) => rawProd.moaRawProdNo === node?.data?.moaRawProdNo);
      if (matchingItem) {
        node.setSelected(true);
      } else {
        node.setSelected(false);
      }
    });
  };

  useEffect(() => {
    if (recipeDetails?.data) setSelectedRawProds(recipeDetails?.data);
    if (recipeDetails?.data) setInitialSelectedRawProds(recipeDetails?.data);
  }, [recipeDetails?.data]);

  const onSelectionChangedAvailable = (
      event: SelectionChangedEvent
  ) => {
    setSelectedRawProds(
      event.api.getSelectedRows().map((row) => {
        const existsInSelected = selectedRawProds.find((rawProd) => rawProd.moaRawProdNo === row.moaRawProdNo);
        return {
          ...row,
          usageAmount: existsInSelected?.usageAmount || 0,
          specNo: existsInSelected?.specNo || +_specNo,
        };
      })
    );
  };

  const onSelectionChangedSelected = (event: SelectionChangedEvent) => {
    selectMatchingAvailableRawProds(event.api.getSelectedRows());
  };

  useEffect(() => {
    if (selectedRawProds) {
      selectedGridRef?.current?.api?.selectAll(); // Select rows from Selected
      selectMatchingAvailableRawProds(selectedRawProds); // Select matching rows from Available
    }
  }, [selectedRawProds, selectedGridRef?.current?.api, availableGridRef?.current?.api]);

  useEffect(() => {
    if (recipe?.data) {
      setFormValues({
        description: recipe.data?.description,
        longDescription: recipe.data?.longDescription,
        fmsProdNo: recipe.data?.fmsProdNo?.toString(),
        statusNo: recipe.data?.statusNo?.toString(),
        moaClassNo: recipe.data?.moaClassNo?.toString(),
        moaBaseNo: recipe.data?.moaBaseNo?.toString(),
        moaSizeNo: recipe.data?.moaSizeNo?.toString(),
        moaProductNo: recipe.data?.moaProductNo?.toString(),
      });
      setInitialFormValues({
        description: recipe.data?.description,
        longDescription: recipe.data?.longDescription,
        fmsProdNo: recipe.data?.fmsProdNo?.toString(),
        statusNo: recipe.data?.statusNo?.toString(),
        moaClassNo: recipe.data?.moaClassNo?.toString(),
        moaBaseNo: recipe.data?.moaBaseNo?.toString(),
        moaSizeNo: recipe.data?.moaSizeNo?.toString(),
        moaProductNo: recipe.data?.moaProductNo?.toString(),
      });
    }
  }, [recipe?.data]);

  useFetchErrorToast([
    { isError: recipeError, label: 'Error fetching recipe', id: 'recipeFetchError' },
    { isError: classesError, label: 'Error fetching classes', id: 'classFetchError' },
    { isError: basesError, label: 'Error fetching bases', id: 'baseFetchError' },
    { isError: sizesError, label: 'Error fetching sizes', id: 'sizeFetchError' },
    { isError: productsError, label: 'Error fetching products', id: 'productFetchError' },
    {
      isError: recipeDetailsIsError,
      error: recipeDetailsError as AxiosError,
      label: 'Error fetching RawProds associated with this recipe',
      id: 'recipeDetailsFetchError',
      ignoredErrors: ['Request failed with status code 404'],
    },
  ]);

  useEffect(() => {
    if ((recipeDetailsError as AxiosError)?.message === 'Request failed with status code 404') {
      setSelectedRawProds([]);
      setInitialSelectedRawProds([]);
    }
  }, [recipeDetailsError]);

  const classOptions = useMemo(() => {
    if (classes?.data) {
      return classes?.data.map((value: Class) => {
        return { value: value.classNo?.toString(), label: value.classNo?.toString() + ' - ' + value.description };
      });
    } else {
      return [];
    }
  }, [classes]);

  const baseOptions = useMemo(() => {
    if (bases?.data) {
      return bases?.data.map((base: ClassBase) => {
        return { value: base.baseNo?.toString(), label: base.baseNo?.toString() + ' - ' + base.baseData?.description };
      });
    } else {
      return [];
    }
  }, [bases]);

  const sizeOptions = useMemo(() => {
    if (sizes?.data) {
      return sizes?.data.map((size: ClassSize) => {
        return { value: size.sizeNo?.toString(), label: size.sizeNo?.toString() + ' - ' + size.sizeData?.description };
      });
    } else {
      return [];
    }
  }, [sizes]);

  const productOptions = useMemo(() => {
    if (products?.data) {
      return products?.data.map((product: Product) => {
        return { value: product.productNo?.toString(), label: product.productNo?.toString() + ' - ' + product.description };
      });
    } else {
      return [];
    }
  }, [products]);

  const handleSelect = (event: ChangeEvent<HTMLSelectElement>) => {
    setFormValues({ ...formValues, [event.target.id]: event.target.value });
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFormValues({ ...formValues, [event.target.id]: event.target.value });
  };
  const handleCellEdit = useCallback(
    (event: any) => {
      let newValue = +event.newValue;
      if (newValue > 9999.9999) newValue = 9999.9999;
      if (newValue < -9999.9999) newValue = -9999.9999;
      if (Number.isNaN(newValue)) newValue = event.oldValue;
      const newSelectedRawProds = selectedRawProds.map((rawProd) => {
        if (rawProd.moaRawProdNo === event.data.moaRawProdNo) {
          return { ...rawProd, usageAmount: newValue };
        } else {
          return rawProd;
        }
      });
      setSelectedRawProds(newSelectedRawProds);
    },
    [selectedRawProds]
  );

  const handleSaveClick = () => {
    setUnsavedChanges(false);
    mutate(
      {
        specNo: +_specNo,
        fmsProdNo: +formValues.fmsProdNo,
        statusNo: +formValues.statusNo,
        description: formValues.description,
        longDescription: formValues.longDescription,
        moaClassNo: +formValues.moaClassNo,
        moaBaseNo: +formValues.moaBaseNo,
        moaSizeNo: +formValues.moaSizeNo,
        moaProductNo: +formValues.moaProductNo,
        recipeDetailDtoList: selectedRawProds.map((rawProd) => {
          return { ...rawProd, specNo: +_specNo };
        }),
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries([
            ['recipes', specNo],
            ['recipeDetails', specNo],
          ]);
          showToast({
            label: 'Recipe has been updated',
            id: 'recipeUpdateSuccess',
          });
          setInitialSelectedRawProds(selectedRawProds);
          setInitialFormValues({ ...formValues });
          refetch();
        },
        onError: (error) => {
          queryClient.invalidateQueries([
            ['recipes', specNo],
            ['recipeDetails', specNo],
          ]);
          if ((error as AxiosError).message === 'apiGatewayTimeout') return;
          showToast(genericErrorShowToastOptions);
        },
      }
    );
  };

  return (
    <>
      <Header breadcrumb={{ label: 'Back To All Recipes', to: '/recipes' }} title={`Spec ${_specNo}`} />
      <RecipeForm>
        <H2WithMargin>Recipe Info</H2WithMargin>
        <RowWrapper>
          <InputWrapperMedium>
            <Input label='Spec No' id='specNo' value={recipe?.data?.specNo?.toString() || ''} disabled />
          </InputWrapperMedium>
          <DropdownWrapperMedium>
            <Dropdown
              label='Status'
              id='statusNo'
              options={Object.keys(STATUS).map((key: any) => {
                return { value: key, label: `${key} - ${STATUS[key as StatusKeys]}` };
              })}
              value={recipe?.data?.statusNo?.toString()}
              onChange={handleSelect}
            />
          </DropdownWrapperMedium>
        </RowWrapper>
        <RowWrapper>
          <InputWrapperMedium>
            <Input label='Update By' id='updateBy' value={recipe?.data?.updatedBy?.toString() || ''} disabled />
          </InputWrapperMedium>
          <InputWrapperMedium>
            <Input
              label='Last Update'
              id='lastUpdate'
              value={
                recipe?.data?.updatedDate
                  ? new Date(recipe?.data?.updatedDate).toLocaleDateString() +
                    ' ' +
                    new Date(recipe?.data?.updatedDate).toLocaleTimeString()
                  : ''
              }
              disabled
            />
          </InputWrapperMedium>
        </RowWrapper>
        <RowWrapper>
          <InputWrapperMedium>
            <Input label='F Prod No' id='fmsProdNo' value={formValues.fmsProdNo} onChange={handleChange} />
          </InputWrapperMedium>
          <InputWrapperLarge>
            <Input label='Description' id='description' value={formValues.description} onChange={handleChange} />
          </InputWrapperLarge>
          <InputWrapperLarge>
            <Input
              label='Long Description'
              id='longDescription'
              value={formValues.longDescription}
              onChange={handleChange}
            />
          </InputWrapperLarge>
        </RowWrapper>
        <HR />
        <H2WithMargin>Product Classification</H2WithMargin>
        <RowWrapper>
          <DropdownWrapperMedium>
            <Dropdown
              label='Class'
              id='moaClassNo'
              options={classOptions}
              value={formValues.moaClassNo}
              onChange={handleSelect}
            />
          </DropdownWrapperMedium>
          <DropdownWrapperMedium>
            <Dropdown
              label='Base'
              id='moaBaseNo'
              options={baseOptions}
              value={formValues.moaBaseNo}
              onChange={handleSelect}
            />
          </DropdownWrapperMedium>
          <DropdownWrapperMedium>
            <Dropdown
              label='Size'
              id='moaSizeNo'
              options={sizeOptions}
              value={formValues.moaSizeNo}
              onChange={handleSelect}
            />
          </DropdownWrapperMedium>
          <DropdownWrapperMedium>
            <Dropdown
              label='Product'
              id='moaProductNo'
              options={productOptions}
              value={formValues.moaProductNo}
              onChange={handleSelect}
            />
          </DropdownWrapperMedium>
        </RowWrapper>
        <TableRowWrapper>
          <RawProductSelectTable
              ref={availableGridRef}
              title={"Available Raw Products"}
              onSelected={onSelectionChangedAvailable}
              width='100%'
              height={rem(900)}
              rowSelection='multiple'
              rowMultiSelectWithClick={true}
          />
          <TableWrapper>
            <Table
              headerTitle='Selected Raw Products'
              width='100%'
              showHeader={true}
              height={rem(900)}
              rowData={selectedRawProds}
              returnedRecords={selectedRawProds.length}
              ref={selectedGridRef}
              rowSelection='multiple'
              suppressRowClickSelection={true}
              onSelectionChanged={onSelectionChangedSelected}
              onCellEditRequest={handleCellEdit}
              readOnlyEdit={true}
              columnDefs={[
                {
                  maxWidth: 48,
                  field: '',
                  headerName: '',
                  checkboxSelection: true,
                  floatingFilter: false,
                  sortable: false,
                  flex: 1,
                },
                { minWidth: 48, field: 'moaRawProdNo', headerName: 'Raw Product', flex: 1 },
                { minWidth: 48, field: 'moaRawProdDesc', headerName: 'Description', flex: 2 },
                {
                  minWidth: 48,
                  field: 'usageAmount',
                  headerName: 'Usage Amount',
                  editable: true,
                  cellRenderer: EditCellRenderer,
                  flex: 1,
                  resizable: false,
                },
              ]}
            />
          </TableWrapper>
        </TableRowWrapper>
        <SaveButton label='Save' onClick={handleSaveClick} disabled={!unsavedChanges} isLoading={isLoading} />
      </RecipeForm>
      <UnsavedChangesModal unsavedChangesExist={unsavedChanges} />
    </>
  );
};

export default EditRecipe;
